import ItiButton from "@/iti-core-react/components/ItiButton/ItiButton";
import ItiPopUpComponent from "@/iti-core-react/components/ItiPopUp/ItiPopUp";
import ItiTextArea from "@/iti-core-react/components/ItiTextArea/ItiTextArea";
import ItiTextBox from "@/iti-core-react/components/ItiTextBox/ItiTextBox";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { GlossaryV2Dto } from "@/shared/dtos/glossary-v2-dto";
import { formatTimestampWithTime } from "@/utils/format-utils";
import { useDevice } from "@/utils/media-query";
import "./GlossaryDetailsPopUp.scss";

interface IGlossaryDetailsPopUp {
	glossary: GlossaryV2Dto;
	onHide: () => void;
}

export default function GlossaryDetailsPopUp({
	glossary,
	onHide,
}: IGlossaryDetailsPopUp) {
	const { t } = useLanguage();
	const { isDesktop } = useDevice();

	const formatDate = (dateString: string) => {
		return formatTimestampWithTime(dateString) || t("glossary.no-data");
	};

	return (
		<ItiPopUpComponent
			title={t("recycle.view-details")}
			visible={true}
			width={isDesktop ? "800" : "95%"}
			height="auto"
			maxHeight="90%"
			onHide={() => onHide()}
			showCloseButton={false}
		>
			<div className="glossary-details-popup">
				{/* Información básica del glosario */}
				<div className="detail-section">
					<h4>{t("glossary.basic-info")}</h4>
					<div className="detail-grid">
						<div className="detail-item full-width">
							<ItiTextBox
								name="glossaryTitle"
								label={t("glossary.title")}
								value={glossary.title || ""}
								readOnly={true}
								showClearButton={false}
							/>
						</div>
						<div className="detail-item full-width">
							<ItiTextBox
								name="shortDescription"
								label={t("glossary.short-description")}
								value={glossary.shortDescription || t("glossary.no-data")}
								readOnly={true}
								showClearButton={false}
							/>
						</div>
						{glossary.longDescription && (
							<div className="detail-item full-width">
								<ItiTextArea
									name="longDescription"
									label={t("glossary.long-description")}
									value={glossary.longDescription || ""}
									readOnly={true}
									height={80}
								/>
							</div>
						)}
					</div>
				</div>

				{/* Información de fechas */}
				<div className="detail-section">
					<h4>{t("glossary.date-info")}</h4>
					<div className="detail-grid">
						<div className="detail-item">
							<ItiTextBox
								name="createdDate"
								label={t("glossary.created")}
								value={
									glossary.created
										? formatDate(glossary.created)
										: t("glossary.no-data")
								}
								readOnly={true}
								showClearButton={false}
							/>
						</div>
						<div className="detail-item">
							<ItiTextBox
								name="modifiedDate"
								label={t("glossary.modified")}
								value={
									glossary.modified
										? formatDate(glossary.modified)
										: t("glossary.no-data")
								}
								readOnly={true}
								showClearButton={false}
							/>
						</div>
					</div>
				</div>

				{/* Información de propietario y editor */}
				<div className="detail-section">
					<h4>{t("glossary.ownership-info")}</h4>
					<div className="detail-grid">
						<div className="detail-item">
							<ItiTextBox
								name="owner"
								label={t("glossary.owner")}
								value={glossary.owner?.name || t("glossary.no-data")}
								readOnly={true}
								showClearButton={false}
							/>
						</div>
						<div className="detail-item">
							<ItiTextBox
								name="publisher"
								label={t("glossary.publisher")}
								value={glossary.publisher?.name || t("glossary.no-data")}
								readOnly={true}
								showClearButton={false}
							/>
						</div>
					</div>
				</div>

				{/* Configuración y acceso */}
				<div className="detail-section">
					<h4>{t("glossary.settings-info")}</h4>
					<div className="detail-grid">
						<div className="detail-item">
							<ItiTextBox
								name="accessRights"
								label={t("glossary.access-rights")}
								value={glossary.translatedVisibility || t("glossary.no-data")}
								readOnly={true}
								showClearButton={false}
							/>
						</div>
						<div className="detail-item">
							<ItiTextBox
								name="language"
								label={t("glossary.language")}
								value={glossary.language?.name || t("glossary.no-data")}
								readOnly={true}
								showClearButton={false}
							/>
						</div>
					</div>
				</div>

				{/* Información adicional */}
				{(glossary.usage || glossary.example) && (
					<div className="detail-section">
						<h4>{t("glossary.additional-info")}</h4>
						<div className="detail-grid">
							{glossary.usage && (
								<div className="detail-item full-width">
									<ItiTextArea
										name="usage"
										label={t("glossary.usage")}
										value={glossary.usage || ""}
										readOnly={true}
										height={80}
									/>
								</div>
							)}
							{glossary.example && (
								<div className="detail-item full-width">
									<ItiTextArea
										name="example"
										label={t("glossary.example")}
										value={glossary.example || ""}
										readOnly={true}
										height={80}
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Términos y categorías */}
				{((glossary.terms && glossary.terms.length > 0) ||
					(glossary.categories && glossary.categories.length > 0)) && (
					<div className="detail-section">
						<h4>{t("glossary.classification")}</h4>
						<div className="detail-grid">
							{glossary.terms && glossary.terms.length > 0 && (
								<div className="detail-item full-width">
									<ItiTextArea
										name="terms"
										label={t("glossary.terms")}
										value={glossary.terms.map((term) => term.title).join(", ")}
										readOnly={true}
										height={80}
									/>
								</div>
							)}
							{glossary.categories && glossary.categories.length > 0 && (
								<div className="detail-item full-width">
									<ItiTextArea
										name="categories"
										label={t("glossary.categories")}
										value={glossary.categories.join(", ")}
										readOnly={true}
										height={80}
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Botón cerrar */}
				<div className="mb-5 d-flex justify-content-end">
					<ItiButton
						className={"outlined-primary-button bg-hv-secondary"}
						text={t("common.close")}
						onClick={onHide}
					/>
				</div>
			</div>
		</ItiPopUpComponent>
	);
}
